import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft, BarChart3, Database, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import ReferringDomainsReport from './ReferringDomainsReport';
import LinkDirectory from './LinkDirectory';
import CircleTimer from './CircleTimer';
import {
  AHREFS_ATTRIBUTION_URL,
  AHREFS_DR_HELP,
  formatAhrefsDr,
  isAhrefsDrLoading
} from '../utils/linkIntelligenceScores';

const LinkIntelligenceTabs = ({ active }) => (
  <div className="border-b border-gray-200 mb-6">
    <nav className="-mb-px flex space-x-8">
      <Link
        to="/link-intelligence"
        className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
          active === 'competitor'
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
      >
        <BarChart3 className="h-4 w-4" />
        <span>Competitor Link Analysis</span>
      </Link>
      <Link
        to="/link-intelligence/directory"
        className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
          active === 'directory'
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
      >
        <Database className="h-4 w-4" />
        <span>Link Directory</span>
      </Link>
    </nav>
  </div>
);

const fetchStatusLabel = (status) => {
  switch (status) {
    case 'fetching':
      return 'Fetching…';
    case 'completed':
      return 'Complete';
    case 'failed':
      return 'Failed';
    default:
      return 'Not fetched';
  }
};

const formatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n.toLocaleString('en-GB');
};

const fetchStatusClass = (status) => {
  switch (status) {
    case 'fetching':
      return 'bg-amber-100 text-amber-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const LinkIntelligence = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, competitorId } = useParams();
  const isDirectory = location.pathname.startsWith('/link-intelligence/directory');

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  const [projectData, setProjectData] = useState(null);
  const [competitorDomain, setCompetitorDomain] = useState('');
  const [addingCompetitor, setAddingCompetitor] = useState(false);
  const [fetchingCompetitorId, setFetchingCompetitorId] = useState(null);
  const [deletingProject, setDeletingProject] = useState(false);

  const [domainsData, setDomainsData] = useState(null);

  const loadClientsAndProjects = useCallback(async () => {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        axios.get('/api/clients'),
        axios.get('/api/link-intelligence/projects')
      ]);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load link intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await axios.get(`/api/link-intelligence/projects/${projectId}`);
      setProjectData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load project');
      navigate('/link-intelligence');
    }
  }, [projectId, navigate]);

  const loadReferringDomains = useCallback(async () => {
    if (!competitorId) return;
    try {
      const response = await axios.get(`/api/link-intelligence/competitors/${competitorId}/referring-domains`);
      setDomainsData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load referring domains');
      navigate(`/link-intelligence/projects/${projectId}`);
    }
  }, [competitorId, projectId, navigate]);

  useEffect(() => {
    if (isDirectory) {
      setLoading(false);
      return;
    }
    if (!projectId && !competitorId) {
      loadClientsAndProjects();
      return;
    }
    setLoading(false);
  }, [projectId, competitorId, isDirectory, loadClientsAndProjects]);

  useEffect(() => {
    if (projectId && !competitorId) {
      setLoading(true);
      loadProject().finally(() => setLoading(false));
    }
  }, [projectId, competitorId, loadProject]);

  useEffect(() => {
    if (competitorId) {
      setLoading(true);
      loadReferringDomains().finally(() => setLoading(false));
    }
  }, [competitorId, loadReferringDomains]);

  const competitors = projectData?.competitors || [];
  const hasFetching = competitors.some((row) => row.fetch_status === 'fetching');
  const hasAhrefsFetching = competitors.some((row) => row.ahrefs_dr_status === 'fetching');
  const ahrefsFetchingOnReport = domainsData?.competitor?.ahrefs_dr_status === 'fetching';

  useEffect(() => {
    if (!projectId || competitorId || (!hasFetching && !hasAhrefsFetching)) return undefined;
    const timer = setInterval(() => {
      loadProject();
    }, 2500);
    return () => clearInterval(timer);
  }, [projectId, competitorId, hasFetching, hasAhrefsFetching, loadProject]);

  useEffect(() => {
    if (!competitorId || !ahrefsFetchingOnReport) return undefined;
    const timer = setInterval(() => {
      loadReferringDomains();
    }, 2000);
    return () => clearInterval(timer);
  }, [competitorId, ahrefsFetchingOnReport, loadReferringDomains]);

  const createProject = async () => {
    if (!selectedClientId) {
      toast.error('Select a client first');
      return;
    }
    setCreatingProject(true);
    try {
      const response = await axios.post('/api/link-intelligence/projects', {
        client_id: Number(selectedClientId)
      });
      toast.success('Link Intelligence project created');
      navigate(`/link-intelligence/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  const addCompetitor = async (e) => {
    e.preventDefault();
    const domain = competitorDomain.trim();
    if (!domain) {
      toast.error('Enter a competitor domain');
      return;
    }
    setAddingCompetitor(true);
    try {
      await axios.post(`/api/link-intelligence/projects/${projectId}/competitors`, { domain });
      setCompetitorDomain('');
      toast.success('Competitor added');
      await loadProject();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add competitor');
    } finally {
      setAddingCompetitor(false);
    }
  };

  const fetchReferringDomains = async (competitorRow) => {
    if (competitorRow.fetch_status === 'fetching') return;
    setFetchingCompetitorId(competitorRow.id);
    try {
      const response = await axios.post(
        `/api/link-intelligence/competitors/${competitorRow.id}/fetch-referring-domains`
      );
      const enriched = response.data?.organic_traffic_updated ?? 0;
      toast.success(
        enriched > 0
          ? `Referring domains fetched (${formatCount(enriched)} with organic traffic)`
          : 'Referring domains fetched'
      );
      if (response.data?.organic_traffic_warning) {
        toast.error(response.data.organic_traffic_warning);
      }
      if (response.data?.summary_warning) {
        toast.error(response.data.summary_warning);
      }
      if (response.data?.history_warning) {
        toast.error(response.data.history_warning);
      }
      if (response.data?.link_gap_warning) {
        toast.error(response.data.link_gap_warning);
      }
      if (response.data?.ahrefs_dr_status === 'fetching') {
        toast.success('Data loaded — Domain Rating from Ahrefs is loading in the background (~4 min)');
      }
      const linkGap = response.data?.link_gap_count ?? 0;
      if (linkGap > 0 && !response.data?.link_gap_warning) {
        toast.success(`${formatCount(linkGap)} domains also link to the client (Link Gap)`);
      }
      await loadProject();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to fetch referring domains');
      await loadProject();
    } finally {
      setFetchingCompetitorId(null);
    }
  };

  const deleteProject = async () => {
    const project = projectData?.project;
    if (!project) return;
    const label = project.client_name || project.name || 'this project';
    const confirmed = window.confirm(
      `Delete project "${label}"?\n\nThis will permanently remove all competitors and referring domain data. This cannot be undone.`
    );
    if (!confirmed) return;
    setDeletingProject(true);
    try {
      await axios.delete(`/api/link-intelligence/projects/${projectId}`);
      toast.success('Project deleted');
      navigate('/link-intelligence');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete project');
    } finally {
      setDeletingProject(false);
    }
  };

  const openCompetitor = (row) => {
    if (row.fetch_status !== 'completed') {
      toast.error('Fetch referring domains first');
      return;
    }
    navigate(`/link-intelligence/projects/${projectId}/competitors/${row.id}`);
  };

  const recentProjects = useMemo(
    () => projects.slice(0, 10),
    [projects]
  );

  if (isDirectory) {
    return (
      <div className="space-y-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Link Intelligence</h1>
          <p className="text-gray-600">Competitor research and the shared guest-post inventory.</p>
        </div>
        <LinkIntelligenceTabs active="directory" />
        <LinkDirectory />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (competitorId && domainsData) {
    return (
      <div className="space-y-2">
        <LinkIntelligenceTabs active="competitor" />
        <ReferringDomainsReport
          projectId={projectId}
          competitorId={competitorId}
          data={domainsData}
        />
      </div>
    );
  }

  if (projectId && projectData) {
    const { project } = projectData;
    return (
      <div className="space-y-6">
        <LinkIntelligenceTabs active="competitor" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.client_name}</h1>
            <p className="text-gray-600">Competitor Link Analysis · {project.name}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/link-intelligence" className="btn btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              All Projects
            </Link>
            <button
              type="button"
              className="btn btn-danger inline-flex items-center gap-2"
              onClick={deleteProject}
              disabled={deletingProject}
            >
              {deletingProject ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Project
            </button>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Add Competitor Site</h2>
          <form onSubmit={addCompetitor} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[16rem]">
              <label className="label" htmlFor="competitor-domain">Competitor domain</label>
              <input
                id="competitor-domain"
                type="text"
                className="input"
                placeholder="e.g. competitor.com"
                value={competitorDomain}
                onChange={(e) => setCompetitorDomain(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary inline-flex items-center gap-2"
              disabled={addingCompetitor}
            >
              {addingCompetitor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add Competitor Site
            </button>
          </form>
        </div>

        <div className="card overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Competitors</h2>
            <button type="button" className="btn btn-secondary inline-flex items-center gap-2" onClick={loadProject}>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <a
                    href={AHREFS_ATTRIBUTION_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-primary-600 hover:underline"
                    title={AHREFS_DR_HELP}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Domain Rating by Ahrefs
                  </a>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain Rank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fetch Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domains Stored</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link Overlap</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {competitors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    No competitors yet. Add a competitor site above.
                  </td>
                </tr>
              ) : (
                competitors.map((row) => {
                  const isFetching = row.fetch_status === 'fetching' || fetchingCompetitorId === row.id;
                  return (
                    <tr
                      key={row.id}
                      className={row.fetch_status === 'completed' ? 'cursor-pointer hover:bg-gray-50' : ''}
                      onClick={() => openCompetitor(row)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.domain}</td>
                      <td className="px-4 py-3 text-sm font-bold text-indigo-700">
                        {isAhrefsDrLoading(row, { ahrefs_dr: row.target_ahrefs_dr }) ? (
                          <CircleTimer />
                        ) : (
                          formatAhrefsDr(row.target_ahrefs_dr)
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-primary-700">
                        {formatCount(row.target_rank)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${fetchStatusClass(row.fetch_status)}`}
                          title={row.fetch_error || ''}
                        >
                          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
                          {fetchStatusLabel(row.fetch_status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.stored_domains_count ?? 0}
                        {row.total_referring_domains ? ` / ${row.total_referring_domains} total` : ''}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-700">
                        {row.fetch_status === 'completed' ? formatCount(row.link_gap_count) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <button
                          type="button"
                          className="btn btn-secondary py-1 px-3"
                          disabled={isFetching}
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchReferringDomains(row);
                          }}
                        >
                          {isFetching ? 'Fetching…' : 'Fetch Referring Domains'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500">
            Click a completed competitor row to view top 200 referring domains and metrics.
            {' '}
            <a href={AHREFS_ATTRIBUTION_URL} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
              Domain Rating by Ahrefs
            </a>
            {' '}
            loads in the background after fetch (~50/min).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Link Intelligence</h1>
        <p className="text-gray-600">
          Research competitor backlink profiles and manage the shared guest-post inventory.
        </p>
      </div>

      <LinkIntelligenceTabs active="competitor" />

      <div className="card max-w-xl">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Competitor Link Analysis Project</h2>
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="li-client-select">Client</label>
            <select
              id="li-client-select"
              className="input"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={createProject}
            disabled={creatingProject || !selectedClientId}
          >
            {creatingProject ? 'Creating…' : 'Create Competitor Link Analysis Project'}
          </button>
        </div>
      </div>

      {recentProjects.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Projects</h2>
          <ul className="divide-y divide-gray-200">
            {recentProjects.map((project) => (
              <li key={project.id}>
                <Link
                  to={`/link-intelligence/projects/${project.id}`}
                  className="block py-3 hover:bg-gray-50 px-2 -mx-2 rounded"
                >
                  <p className="font-medium text-gray-900">{project.client_name}</p>
                  <p className="text-sm text-gray-500">
                    Created {new Date(project.created_at).toLocaleString()}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LinkIntelligence;
